import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSearch } from '../../contexts/SearchContext';
import { api } from '../../lib/api';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { cn } from './Kit';

const { FiSearch, FiUser, FiX } = FiIcons;

export default function GlobalSearchBar() {
  const { globalQuery, setGlobalQuery } = useSearch();
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const ref = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (!ref.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (!globalQuery.trim() || globalQuery.length < 2) {
      setResults([]);
      setOpen(false);
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const all = await api.leads.getAll({});
        const q = globalQuery.toLowerCase();
        const filtered = (all || []).filter(l =>
          `${l.first_name} ${l.last_name}`.toLowerCase().includes(q) ||
          l.phone?.includes(q) ||
          l.email?.toLowerCase().includes(q)
        ).slice(0, 6);
        setResults(filtered);
        setOpen(true);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
  }, [globalQuery]);

  const handleSelect = (lead) => {
    setGlobalQuery('');
    setOpen(false);
    navigate(`/leads/${lead.id}`);
  };

  const handleClear = () => {
    setGlobalQuery('');
    setOpen(false);
  };

  const handleViewAll = () => {
    setOpen(false);
    navigate('/leads');
  };

  return (
    <div ref={ref} className="relative w-full max-w-sm">
      <div className="relative">
        <SafeIcon
          icon={FiSearch}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none"
        />
        <input
          type="text"
          value={globalQuery}
          onChange={(e) => setGlobalQuery(e.target.value)}
          onFocus={() => results.length > 0 && setOpen(true)}
          placeholder="Search leads by name, phone, email…"
          className="w-full h-9 pl-9 pr-8 text-sm rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
        />
        {globalQuery && (
          <button onClick={handleClear} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
            <SafeIcon icon={FiX} className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {open && (
        <div className="absolute top-full mt-1.5 left-0 right-0 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden z-50">
          {loading ? (
            <div className="px-4 py-3 text-sm text-gray-400 text-center">Searching…</div>
          ) : results.length === 0 ? (
            <div className="px-4 py-3 text-sm text-gray-400 text-center">No leads found</div>
          ) : (
            <>
              {results.map(lead => (
                <button
                  key={lead.id}
                  onClick={() => handleSelect(lead)}
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition-colors text-left"
                >
                  <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <SafeIcon icon={FiUser} className="w-3.5 h-3.5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {lead.first_name} {lead.last_name}
                    </p>
                    <p className="text-xs text-gray-400 truncate">{lead.phone || lead.email}</p>
                  </div>
                </button>
              ))}
              <button
                onClick={handleViewAll}
                className="w-full px-4 py-2 text-xs text-primary font-medium bg-gray-50 hover:bg-gray-100 text-center border-t border-gray-100 transition-colors"
              >
                View all results in Leads →
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}